import { useState } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { Eraser, Info } from 'lucide-react';
import { isAddressableKind, isReplaceableKind, getAddrCoord } from '@/hooks/useTombstone';

interface NullifyEventDialogProps {
  event: NostrEvent;
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNullified?: () => void;
}

export function NullifyEventDialog({
  event,
  label,
  open,
  onOpenChange,
  onNullified,
}: NullifyEventDialogProps) {
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();

  const canNullify = isAddressableKind(event.kind) || isReplaceableKind(event.kind);

  const handleNullify = async () => {
    if (!canNullify) return;

    try {
      const now = Math.floor(Date.now() / 1000);

      // 1. Publish nullified replacement (aggressive approach)
      const preservedTags: string[][] = [];

      // Keep ONLY the 'd' tag for addressable events (required to target the specific event)
      if (isAddressableKind(event.kind)) {
        const dTag = event.tags.find(([name]) => name === 'd');
        if (dTag) preservedTags.push(dTag);
      }

      // Add explicit nullification metadata
      preservedTags.push(['alt', 'Nullified by Tombstone']);
      preservedTags.push(['client', 'tombstone']);

      await publish({
        kind: event.kind,
        content: 'Nullified by Tombstone',
        tags: preservedTags,
        created_at: now,
      });

      // 2. Also publish a NIP-09 deletion request (for clients/relays that honor kind 5)
      const deletionTags: string[][] = [['k', String(event.kind)]];

      const addrCoord = getAddrCoord(event);
      if (addrCoord) {
        deletionTags.push(['a', addrCoord]);
      } else {
        deletionTags.push(['e', event.id]);
      }

      await publish({
        kind: 5,
        content: 'Nullified via Tombstone',
        tags: deletionTags,
        created_at: now + 1, // slightly newer
      });

      toast({
        title: 'Event nullified',
        description: 'Published both a nullified replacement (empty content) and NIP-09 deletion request. This maximizes coverage across relays and clients.',
      });

      onNullified?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Failed to nullify',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded flex items-center justify-center bg-accent/10 border border-accent/20">
              <Eraser className="w-4 h-4 text-accent" />
            </div>
            <DialogTitle className="font-display text-base">
              Nullify {label}
            </DialogTitle>
          </div>
            <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
              Aggressively wipes this event by publishing a <span className="text-foreground font-medium">nullified replacement (with minimal metadata)</span> 
              and a NIP-09 deletion request. Maximizes coverage on relays and clients.
            </DialogDescription>

        </DialogHeader>

        {/* Info box */}
        <div className="rounded border border-accent/20 bg-accent/5 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-accent flex-shrink-0" />
            <span className="text-xs font-medium text-accent">How nullification works</span>
          </div>
             <p className="text-xs text-muted-foreground leading-relaxed">
               For replaceable and addressable events, we publish <strong>two events</strong>: 
               (1) a replacement with minimal metadata (only <code className="text-accent">d</code>, <code>alt</code>, and <code>client</code> tags) 
               and content saying it has been nullified, and (2) a NIP-09 deletion request.
               This gives maximum coverage against relays and clients that ignore deletions.
             </p>

        </div>

        {!canNullify && (
          <div className="rounded border border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
            Nullification is only available for <strong>replaceable</strong> and <strong>addressable</strong> events
            (kinds 10000–19999, 30000–39999, and kind 0/3). Regular notes (kind 1) cannot be nullified this way.
          </div>
        )}

        {canNullify && (
          <div className="rounded border border-border bg-secondary/30 p-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Kind</span>
              <span className="text-xs text-foreground">{event.kind} ({label})</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">New content</span>
              <span className="text-xs text-muted-foreground italic">"This content has been nullified by Tombstone."</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Tags kept</span>
              <span className="text-xs text-muted-foreground">Only d-tag + alt + client</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Actions</span>
              <span className="text-xs text-accent font-medium">Aggressive nullification + NIP-09 deletion</span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="text-muted-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleNullify}
            disabled={isPending || !canNullify}
            className="bg-accent text-accent-foreground hover:bg-accent/90"
          >
            {isPending ? (
              <>
                <span className="mr-2 w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                Publishing…
              </>
            ) : (
              <>
                <Eraser className="w-3.5 h-3.5 mr-2" />
                Nullify Event
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
